<template>
  <v-navigation-drawer
    :model-value="modelValue"
    :width="drawerWidth"
    :temporary="mobile"
    :permanent="!mobile"
    class="custom-drawer"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="drawer-header">
      <div class="image-container">
        <v-img
          :src="require('@/assets/brenden_zoom.png')"
          :aspect-ratio="1"
          :cover="true"
          class="drawer-image"
        />
      </div>
    </div>

    <div class="drawer-content">
      <v-list class="navigation-list">
        <v-list-item
          v-for="(item, index) in navigationItems"
          :key="index"
          :to="item.link"
          class="nav-item"
          density="comfortable"
        >
          <div class="nav-item-content">
            <span class="nav-text">{{ item.title }}</span>
          </div>
        </v-list-item>
      </v-list>
    </div>

    <div class="social-links">
      <a
        v-for="(item, index) in socialItems"
        :key="index"
        :href="item.link"
        :target="
          item.link.includes('github.com') || item.link.includes('linkedin.com')
            ? '_blank'
            : '_self'
        "
        rel="noopener noreferrer"
        class="social-link"
      >
        <v-icon class="social-icon">{{ item.icon }}</v-icon>
      </a>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { useDisplay } from 'vuetify';

export default {
  name: 'core-drawer',
  components: {},
  props: {
    modelValue: Boolean,
  },
  emits: ['update:modelValue'],
  setup() {
    const { mobile, width } = useDisplay();

    return { mobile, viewportWidth: width };
  },
  data: () => ({
    navigationItems: [
      {
        title: 'About',
        link: '/about',
      },
      {
        title: 'Projects',
        link: '/projects',
      },
      {
        title: 'Resume',
        link: '/resume',
      },
      {
        title: 'Writing',
        link: '/writing',
      },
    ],
    socialItems: [
      {
        link: 'https://github.com/bdog18',
        icon: 'mdi-github',
      },
      {
        link: 'https://www.linkedin.com/in/brenden-runion',
        icon: 'mdi-linkedin',
      },
      {
        link: 'mailto:brendenrunion@gmail.com',
        icon: 'mdi-email',
      },
    ],
  }),
  computed: {
    // Vuetify reserves this many pixels for the drawer in the app layout, so
    // the width has to be a number rather than a CSS clamp().
    drawerWidth() {
      if (this.mobile) {
        return Math.min(280, Math.round(this.viewportWidth * 0.8));
      }
      return Math.round(Math.min(240, Math.max(180, this.viewportWidth * 0.2)));
    },
  },
};
</script>

<style scoped>
.custom-drawer {
  background: linear-gradient(
    180deg,
    rgb(var(--v-theme-surface)) 0%,
    rgba(var(--v-theme-primary), 0.02) 100%
  ) !important;
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  backdrop-filter: blur(10px);
}

/* Lay the drawer out as a column so the social links sit against the
   bottom at any viewport height, rather than being absolutely positioned
   where they can overlap the nav on a short screen. */
.custom-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
}

.drawer-header {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.1) 0%,
    rgba(var(--v-theme-secondary), 0.05) 100%
  );
  padding: clamp(0.75rem, 3vw, 1.5rem);
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  flex-shrink: 0;
}

.image-container {
  display: flex;
  justify-content: center;
}

.drawer-image {
  width: 100%;
  max-width: 150px;
  border-radius: 8px;
  border: 3px solid rgba(var(--v-theme-on-surface), 0.2);
  box-shadow: 0 4px 20px rgba(var(--v-theme-primary), 0.2);
  transition: all 0.3s ease;
}

.drawer-image:hover {
  transform: scale(1.02);
  border-color: rgba(var(--v-theme-primary), 0.5);
  box-shadow: 0 6px 25px rgba(var(--v-theme-primary), 0.3);
}

.drawer-content {
  padding: 1rem 0;
}

.navigation-list {
  background: transparent !important;
  padding: 0 0.5rem;
}

.nav-item {
  margin-bottom: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: transparent !important;
}

.nav-item:hover {
  background: rgba(var(--v-theme-primary), 0.1) !important;
  transform: translateX(4px);
}

.nav-item-content {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0.5rem;
}

.nav-text {
  font-size: var(--font-body);
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  text-decoration: none;
  transition: color 0.3s ease;
}

.nav-item:hover .nav-text {
  color: rgb(var(--v-theme-link));
  font-weight: 600;
}

.social-links {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(0.5rem, 2vw, 1rem);
  padding: 1rem;
}

.social-link {
  color: rgb(var(--v-theme-on-surface));
  text-decoration: none;
  padding: clamp(0.5rem, 2vw, 0.75rem);
  border-radius: 12px;
  transition: all 0.3s ease;
  background: rgba(var(--v-theme-surface), 0.5);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  backdrop-filter: blur(5px);
}

.social-link:hover {
  background: rgba(var(--v-theme-primary), 0.1);
  border-color: rgba(var(--v-theme-primary), 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(var(--v-theme-primary), 0.2);
}

.social-icon {
  font-size: 1.25rem !important;
  color: rgb(var(--v-theme-on-surface));
  transition: color 0.3s ease;
}

.social-link:hover .social-icon {
  color: rgb(var(--v-theme-link));
}
</style>
